import React, { createContext, useContext, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'

import {
  Box,
  Button,
  Checkbox,
  Flex,
  Header,
  IconMenuButton,
  InfoBadge,
  Text,
} from '@driftt/dds'
import { access } from '@driftt/traffic-control'

import { DeserializedSavedFilter } from 'api/AccountActivityAPI/types'
import useNotificationsFilters from 'api/useNotificationsFilters'
import { useAttributeFilters } from 'hooks/useAttributeFilters'
import useFilterConfiguration from 'hooks/useFilterConfiguration'
import useNotifications from 'hooks/useNotifications'
import { useAppSelector } from 'hooks/useRedux'
import useSavedFilters from 'hooks/useSavedFilters'
import {
  FilterGroup,
} from 'utils/filters'
import { captureExceptionWithMessage } from 'utils/logRocket'
import { segmentTrack, TrackType } from 'utils/tracking'

import { getSavedFilterDescription } from './utils'

interface FilterSettingsContextProps {
  openDeleteModal: (filter: DeserializedSavedFilter) => void
  closeDeleteModal: () => void
  savedFilter: DeserializedSavedFilter | null
  setSavedFilter: (filter: DeserializedSavedFilter | null) => void
  openEditModal: (filter: DeserializedSavedFilter) => void
}

const FilterSettingsContext = createContext<FilterSettingsContextProps>({
  openDeleteModal: () => {return},
  closeDeleteModal: () => {return},
  savedFilter: null,
  setSavedFilter: () => {return},
  openEditModal: () => {return}
})

interface FilterNotificationSettingsProps {
  filterId: number
}

const FilterNotificationSettings = ({ filterId }: FilterNotificationSettingsProps) => {
  const { success } = useNotifications()
  const { filterSettings, handleUpdateFilterSettings } = useNotificationsFilters()
  const user = useSelector((state: any) => state.user)

  const [email, setEmail] = useState(false)
  const [desktop, setDesktop] = useState(false)

  useEffect(() => {
    if (filterSettings?.length) {
      const filterSetting = filterSettings.find((setting: any) => setting.id === filterId)
      if (filterSetting) {
        setEmail(filterSetting?.hasEmail ?? false)
        setDesktop(filterSetting?.hasDesktop ?? false)
      }
    }
  }, [filterId, filterSettings])

  const updateFilterSettings = async ({
    hasEmail,
    hasDesktop
  }: {
    hasEmail: boolean
    hasDesktop: boolean
  }) => {
    if (!filterSettings?.length) return

    const payload = filterSettings.map(filter => {
      const newFilter = {
        savedFilterSetId: filter.id.toString(),
        hasEmail: filter.hasEmail,
        hasDesktop: filter.hasDesktop,
        hasMobile: filter.hasMobile,
        userId: user.id
      }

      if (filter.id === filterId) {
        return {
          ...newFilter,
          hasEmail,
          hasDesktop
        }
      }

      return newFilter
    })

    try {
      handleUpdateFilterSettings(payload as any)
      success(`Your notification settings have been saved.`)
      segmentTrack(TrackType.FILTERS_SETTINGS_MODAL_CHANGE_NOTIFICATION_SETTINGS)
    } catch (error) {
      captureExceptionWithMessage('Error updating notification settings', error, {
        user
      })
    }
  }

  const handleEmailChange = (value: boolean) => {
    setEmail(value)
    updateFilterSettings({ hasEmail: value, hasDesktop: desktop })
  }

  const handleDesktopChange = (value: boolean) => {
    setDesktop(value)
    updateFilterSettings({ hasEmail: email, hasDesktop: value })
  }

  return (
    <Flex
      gap={4}
      alignItems="center"
      justifyContent="center"
      css={{
        marginRight: '$6'
      }}>
      <Checkbox label="Email" checked={email} onChange={handleEmailChange} />
      <Checkbox label="Desktop" checked={desktop} onChange={handleDesktopChange} />
    </Flex>
  )
}

interface FilterSettingMenuProps {
  filter: DeserializedSavedFilter
  isFilterOwner?: boolean
  isDefaultFilter?: boolean
}

const FilterSettingMenu = ({
  filter,
  isFilterOwner = false,
  isDefaultFilter = false
}: FilterSettingMenuProps) => {
  const { updateDefaultFilterId, deleteDefaultFilter } = useSavedFilters()
  const { openDeleteModal } = useContext(FilterSettingsContext)

  const changeDefaultFilter = () => {
    isDefaultFilter ? deleteDefaultFilter() : updateDefaultFilterId(filter.id)
    segmentTrack(TrackType.FILTERS_SETTINGS_MODAL_CHANGE_DEFAULT_FILTER)
  }

  const options = [
    {
      action: changeDefaultFilter,
      name: isDefaultFilter ? 'Remove as default filter' : 'Set as my default saved filter'
    }
  ]

  const hasOrgSettingsEnabled = access.hasFeatures([
    { featureName: 'filter_admin_settings', accessLevel: 'WRITE' }
  ])

  if (isFilterOwner || hasOrgSettingsEnabled) {
    options.push({
      action: () => {
        openDeleteModal(filter)
        segmentTrack(TrackType.FILTERS_SETTINGS_MODAL_DELETE_FILTER_MODAL_OPEN)
      },
      name: 'Delete saved filter'
    })
  }

  return <IconMenuButton iconVariant="spillover" options={options} title="" />
}

interface SavedFilterSettingProps {
  savedFilter: DeserializedSavedFilter
  isFilterOwner?: boolean
}

const SavedFilterSetting = ({ savedFilter, isFilterOwner }: SavedFilterSettingProps) => {
  const { openEditModal } = useContext(FilterSettingsContext)
  const { defaultFilterId } = useAppSelector(({ filters }) => filters)
  
  const attributeFilters = useAttributeFilters()
  const filterConfiguration = useFilterConfiguration(attributeFilters)

  const hasOrgSettingsEnabled = access.hasFeatures([
    { featureName: 'filter_admin_settings', accessLevel: 'WRITE' }
  ])

  const shouldDisableEditButton = !isFilterOwner && !hasOrgSettingsEnabled
  const isDefaultFilter = defaultFilterId === savedFilter.id && defaultFilterId !== 0
  return (
    <Box
      css={{
        borderBottom: '1px solid $gray3',
        width: '100%',
        padding: '$2',
        '&:last-child': {
          borderBottom: 'none'
        }
      }}>
      <Flex alignItems="center" justifyContent="space-between">
        <Flex direction="column" gap={0}>
          <Flex alignItems="center">
            <Header variant="h3">{savedFilter.name}</Header>
            {isDefaultFilter && <InfoBadge>Default</InfoBadge>}
          </Flex>
          <Text variant="metadata" css={{ fontStyle: 'italic' }}>
            {getSavedFilterDescription(filterConfiguration, savedFilter)}
          </Text>
        </Flex>
        <Flex alignItems="center">
          <FilterNotificationSettings filterId={savedFilter.id} />
          <Button
            variant="secondary"
            size="small"
            css={{ width: '$6' }}
            onClick={() => {
              openEditModal(savedFilter)
              segmentTrack(TrackType.FILTERS_SETTINGS_MODAL_EDIT_FILTER_MODAL_OPEN)
            }}
            disabled={shouldDisableEditButton}>
            Edit
          </Button>
          <FilterSettingMenu
            filter={savedFilter}
            isFilterOwner={isFilterOwner}
            isDefaultFilter={isDefaultFilter}
          />
        </Flex>
      </Flex>
    </Box>
  )
}

interface SavedFiltersGroupProps {
  savedFilters: DeserializedSavedFilter[]
  isPrivateFilter?: boolean
}

const SavedFiltersGroup = ({ isPrivateFilter = false, savedFilters }: SavedFiltersGroupProps) => {
  return (
    <Box css={{ marginBottom: '$1' }}>
      <Flex justifyContent="space-between" css={{ paddingBottom: '$1', paddingTop: '$1' }}>
        <Header variant="h4">
          {isPrivateFilter ? 'MY SAVED FILTERS' : 'SHARED SAVED FILTERS'}
        </Header>
        <Header variant="h4" css={{ marginRight: '15rem' }}>
          MY NOTIFICATIONS
        </Header>
      </Flex>
      <Flex
        direction="column"
        gap={0}
        css={{
          border: '1px solid $gray3',
          borderRadius: '$1',
          borderColor: '$gray3'
        }}>
        {savedFilters.map(savedFilter => (
          <SavedFilterSetting key={savedFilter.id} savedFilter={savedFilter} isFilterOwner={isPrivateFilter} />
        ))}
      </Flex>
    </Box>
  )
}

interface ManageSavedFiltersModalProps {
  sharedFilters: DeserializedSavedFilter[]
  privateFilters: DeserializedSavedFilter[]
  openDeleteModal: (filter: DeserializedSavedFilter) => void
  closeDeleteModal: () => void
  setSavedFilter: (filter: DeserializedSavedFilter | null) => void
  savedFilter: DeserializedSavedFilter | null
  openEditModal: (filter?: DeserializedSavedFilter, editableGroups?: FilterGroup[]) => void
}

const ManageSavedFilters = ({
  sharedFilters,
  privateFilters,
  openDeleteModal,
  closeDeleteModal,
  setSavedFilter,
  savedFilter,
  openEditModal,
}: ManageSavedFiltersModalProps) => {  
  return (
    <FilterSettingsContext.Provider
      value={{
        openDeleteModal,
        closeDeleteModal,
        setSavedFilter,
        savedFilter,
        openEditModal,
      }}>
      <SavedFiltersGroup savedFilters={privateFilters} isPrivateFilter />
      <SavedFiltersGroup savedFilters={sharedFilters} />
    </FilterSettingsContext.Provider>
  )
}

export default ManageSavedFilters
